import { ButtonOutlinedPrimary } from 'components/Shared/Button';
import FlexBox from 'components/Shared/Container/FlexBox';
import Icon from 'components/Shared/Icon/Icon';
import ListItem from 'components/Shared/ListItem';
import RawMaterialFilter from 'components/Shared/RawMaterialFilter/RawMaterialFilter';
import { StackedArea, useStackableContent, } from 'components/Shared/StackedContent/StackableContentContext';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';
import { CoordinateFeature } from 'types/site.types';
import { getBreadcrumb } from 'utils/breadcrumbUtils';
import { ActivityContainer } from '../Activities';
import { useActiveContext } from '../Context/ActiveContext';
import { ProductJourneyPages } from '../pages';
import MobileImpactClaimsSlide from './MobileImpactClaimsSlide';
import MobileSiteOverviewSlide from './MobileSiteOverviewSlide';
import MobileBatchDetailsSlide from './MobileBatchDetailsSlide';
import BatchTracing from 'components/Shared/BatchTracingContainer/BatchTracing';
import { X as Close } from '@styled-icons/bootstrap/X';
const Container = styled.div `
  min-height: 100%;
  height: 100%;
  width: 100%;
  padding: 0 10px;
`;
const PositionedContainer = styled.div `
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const ScrollContainer = styled(FlexBox) `
  flex: 1;
  padding-bottom: 8px;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  height: auto;
`;
const AnimationContainer = styled(animated.div) `
  will-change: max-height;
  display: flex;
  width: 100%;
`;
const ActionButtonContainer = styled.div `
  display: flex;
  margin-bottom: 16px;
  margin-top: ${({ marginTop }) => (marginTop ? `${marginTop}px` : 0)};
`;
const Title = styled.span `
  color: #000;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 900;
  line-height: 20px;
  letter-spacing: -0.32px;
`;
const ContainerBatchTracing = styled.div `
  margin-bottom: 16px;
  width: 100%;
`;
const MobileActivitySlide = ({ steps, scrollToTop, showProductJourneyCards = true, mobile, }) => {
    const { initialOriginSelection, initialRawMaterialSelection, activeStepIndex, activeStepActivity, activeStep, showRawMaterialFilter, allRawMaterials, selectedRawMaterialId, batchTracingPossible, isBatchTracingActive, allSelectedStepActivityRawMaterialIds, hasSelectedStepActivityOtherIngredients, isSelectedActivityTraceable, isSelectedStepTraceable, handleSelectedRawMaterialChange, handleTraceMyBatchClick, handleSelectedActivityChange, handleStopBatchTracingClick, handleGoToBatchDataStep, } = useActiveContext();
    const { t } = useTranslation();
    const { activateStackedContent } = useStackableContent();
    const isOriginStep = activeStepIndex === 0;
    const selectedRawMaterial = useMemo(() => allRawMaterials.find((rawMaterial) => rawMaterial.id === selectedRawMaterialId), [allRawMaterials, selectedRawMaterialId]);
    const styles = useSpring({
        maxHeight: showProductJourneyCards ? 3000 : 0,
    });
    const handleNodeClick = (id) => {
        handleSelectedActivityChange(id);
    };
    const openImpactClaimDetailSlide = () => {
        activateStackedContent({
            renderComponent: () => (React.createElement(MobileImpactClaimsSlide, { key: ProductJourneyPages.IMPACT_CLAIM_LIST, activity: activeStepActivity.activity, steps: steps, scrollToTop: scrollToTop, mobile: mobile })),
            id: ProductJourneyPages.IMPACT_CLAIM_LIST,
            area: StackedArea.PAGE,
            isActive: true,
            layout: {
                hasBackButton: true,
                hasHeader: true,
                hasHeaderScrollEffect: false,
                hasFooter: true,
            },
            breadcrumb: getBreadcrumb(ProductJourneyPages.IMPACT_CLAIM_LIST, t),
        });
    };
    const openActivitySitesClick = () => {
        activateStackedContent({
            renderComponent: () => (React.createElement(MobileSiteOverviewSlide, { chainStepActivity: activeStepActivity, isSelectedActivityTraceable: isSelectedActivityTraceable, isSelectedStepTraceable: isSelectedStepTraceable, hasOtherIngredients: hasSelectedStepActivityOtherIngredients, isBatchTracingActive: isBatchTracingActive, selectedRawMaterial: selectedRawMaterial })),
            id: ProductJourneyPages.SITE_CLUSTER_OVERVIEW,
            area: StackedArea.PAGE,
            isActive: true,
            layout: {
                hasBackButton: true,
                hasHeader: true,
                hasHeaderScrollEffect: false,
                hasFooter: true,
            },
            breadcrumb: getBreadcrumb(ProductJourneyPages.SITE_CLUSTER_OVERVIEW, t, activeStepIndex !== 0 ? t('site') : undefined),
        });
    };
    const openBatchDetailsSlide = () => {
        activateStackedContent({
            renderComponent: () => (React.createElement(MobileBatchDetailsSlide, { isOriginStep: isOriginStep, isSelectedActivityTraceable: isSelectedActivityTraceable, isSelectedStepTraceable: isSelectedStepTraceable, activeStepActivity: activeStepActivity, hasOtherIngredients: hasSelectedStepActivityOtherIngredients, selectedRawMaterial: selectedRawMaterial })),
            id: ProductJourneyPages.BATCH_TRACING,
            area: StackedArea.PAGE,
            isActive: true,
            layout: {
                hasBackButton: true,
                hasHeader: true,
                hasHeaderScrollEffect: false,
                hasFooter: true,
            },
            breadcrumb: getBreadcrumb(ProductJourneyPages.BATCH_TRACING, t),
        });
    };
    if (initialRawMaterialSelection) {
        return (React.createElement(Container, { className: "st-mobile-only" },
            React.createElement(PositionedContainer, null,
                React.createElement(AnimationContainer, { style: styles },
                    React.createElement(ScrollContainer, null,
                        React.createElement(FlexBox, { alignItems: "flex-start", gap: 2, flexDirection: "column", mb: 2, mt: 3, width: "100%" },
                            React.createElement(Title, null, t('select-ingredient-view-more')),
                            React.createElement(FlexBox, { gap: 1, flexDirection: "column", width: "100%" },
                                React.createElement(ListItem, { title: t('all-ingredients'), onClick: () => handleSelectedRawMaterialChange(undefined) }),
                                allRawMaterials.map(({ id, title, imageUrl }) => (React.createElement(ListItem, { key: id, imageUrl: imageUrl, title: title, onClick: () => handleSelectedRawMaterialChange(id) }))))),
                        batchTracingPossible && !isBatchTracingActive && (React.createElement(ActionButtonContainer, null,
                            React.createElement(ButtonOutlinedPrimary, { onClick: handleTraceMyBatchClick },
                                React.createElement(Icon, { name: "tracing" }),
                                t('trace-my-batch')))))))));
    }
    if (initialOriginSelection) {
        return (React.createElement(Container, { className: "st-mobile-only" },
            React.createElement(PositionedContainer, null,
                React.createElement(AnimationContainer, { style: styles },
                    React.createElement(ScrollContainer, null,
                        React.createElement(FlexBox, { alignItems: "flex-start", gap: 2, flexDirection: "column", mb: 2, mt: 3, width: "100%" },
                            React.createElement(Title, null, t('select-origin-view')),
                            React.createElement(FlexBox, { gap: 1, flexDirection: "column", width: "100%" }, activeStep === null || activeStep === void 0 ? void 0 : activeStep.chainStepActivities.map(({ id, activity: { component, site: { locationName, coordinateFeatures }, }, }) => {
                                var _a;
                                return (React.createElement(ListItem, { key: id, tag: component === null || component === void 0 ? void 0 : component.title, iconName: "pin-selected", title: ((_a = coordinateFeatures === null || coordinateFeatures === void 0 ? void 0 : coordinateFeatures.find(({ featureType }) => CoordinateFeature.COUNTRY === featureType)) === null || _a === void 0 ? void 0 : _a.locationName) || locationName, onClick: () => handleSelectedActivityChange(id) }));
                            }))),
                        batchTracingPossible && !isBatchTracingActive && (React.createElement(ActionButtonContainer, null,
                            React.createElement(ButtonOutlinedPrimary, { onClick: handleTraceMyBatchClick },
                                React.createElement(Icon, { name: "tracing" }),
                                t('trace-my-batch')))))))));
    }
    return (React.createElement(Container, { className: "st-mobile-only" },
        React.createElement(PositionedContainer, null,
            React.createElement(AnimationContainer, { style: styles },
                React.createElement(ScrollContainer, { mt: 2 },
                    showRawMaterialFilter && (React.createElement(RawMaterialFilter, { variant: isBatchTracingActive ? 'primary' : 'default', items: allRawMaterials, hideAllSelection: isBatchTracingActive, selectedId: selectedRawMaterialId, highlightedIds: allSelectedStepActivityRawMaterialIds, onSelectionChange: handleSelectedRawMaterialChange })),
                    isBatchTracingActive && (React.createElement(ContainerBatchTracing, null,
                        React.createElement(BatchTracing, { hasOtherIngredients: hasSelectedStepActivityOtherIngredients, rawMaterial: selectedRawMaterial, batchTracingActive: isBatchTracingActive, notTraceableActivity: !isSelectedActivityTraceable, notTraceableStep: !isSelectedStepTraceable, chainStepActivity: activeStepActivity, isOverview: true, onBatchTracingStart: handleTraceMyBatchClick, onBatchTracingEnd: handleStopBatchTracingClick, onClick: openBatchDetailsSlide, onGoToFirstBatchDataStep: handleGoToBatchDataStep }))),
                    !!(activeStep === null || activeStep === void 0 ? void 0 : activeStep.chainStepActivities) && (React.createElement(ActivityContainer, { activeStepIndex: activeStepIndex, activeChainActivity: activeStepActivity, chainStepActivities: (activeStep === null || activeStep === void 0 ? void 0 : activeStep.chainStepActivities) || [], isMobile: true, onImpactClaimClick: openImpactClaimDetailSlide, setActiveNode: handleNodeClick, onActivitySitesClick: openActivitySitesClick })),
                    batchTracingPossible && (React.createElement(ActionButtonContainer, null, isBatchTracingActive ? (React.createElement(ButtonOutlinedPrimary, { onClick: handleStopBatchTracingClick },
                        React.createElement(Close, { size: 24 }),
                        t('exit'))) : (React.createElement(ButtonOutlinedPrimary, { onClick: handleTraceMyBatchClick },
                        React.createElement(Icon, { name: "tracing" }),
                        t('trace-my-batch'))))))))));
};
export default MobileActivitySlide;
